import React, {FC, useState, useRef, useEffect} from "react";

import {Table, Space, Modal, Dropdown, Menu, Button, message, Tree, TreeProps, Spin} from 'antd';
import { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import type { ProDescriptionsActionType } from "@ant-design/pro-descriptions";

import {DownOutlined, RedoOutlined, } from '@ant-design/icons';

import type {Pagination} from './../../../../utils/data/Type'
import { Organization } from './../../type/OrganizationType';
import OrganizationApi from "../../api/OrganizationApi";
import OrganizationAddForm from "./OrganizationAddForm";
import OrganizationDetail from "./OrganizationDetail";
import ProCard from "@ant-design/pro-card";
import { resolve } from "path";


const OrganizationTable:React.FC<{
    refreshTreeData:()=>void,
    rootId: string|undefined,
}> = (props)=>{

    const {rootId, refreshTreeData} = props;

    const tableActionRef = useRef<ActionType>();
    const detailActionRef = useRef<ProDescriptionsActionType>();
    const [addFormVisible, setAddFormVisible] = useState<boolean>(false);
    const [detailVisible, setDetailVisible] = useState<boolean>(false);
    const [currentOrganization, setCurrentOrganization] = useState<Organization>({});

    const refreshData = ()=>{
        tableActionRef.current?.reload();
        refreshTreeData();
    }

    useEffect(()=>{
        tableActionRef.current?.reload();
    }, [rootId]);

    const deleteOrganization = (organization:Organization)=>{
        Modal.confirm({
            title: "删除机构确认",
            content: "确定删除该机构吗？",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                await OrganizationApi.deleteOrganization(organization);
                refreshData();
            }
        });
    }

    const showAddForm = ()=>{
        setAddFormVisible(true);
    }

    const hideAddForm = ()=>{
        setAddFormVisible(false);
    }

    const showDetail = (organization:Organization)=>{
        setCurrentOrganization(organization);
        setDetailVisible(true);
    }

    const hideDetail = ()=>{
        setCurrentOrganization({});
        setDetailVisible(false);
    }

    const rowExtraButtons : React.FC<{
        item: Organization
    }> = ({item}) => {
        return (
            <Dropdown
                key="row-extra-btns"
                overlay={
                    <Menu
                        onClick={({key})=>{
                            if(key==='delete') {
                                deleteOrganization(item);
                            }
                        }}
                    >
                        <Menu.Item key="delete">
                            删除
                        </Menu.Item>
                    </Menu>
                }
            >
                <a>
                    更多 <DownOutlined />
                </a>
            </Dropdown>
        );
    };

    const columns:ProColumns<Organization>[] = [
        {
            key: "rowNo",
            title: "序号",
            dataIndex: "rowNo",
            width: 60,
            hideInSearch: true,
            hideInSetting: true,
            hideInForm: true,
            copyable: false,
        },
        {
            key: "parentId",
            title: "上级机构ID",
            dataIndex: "parentId",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "code",
            title: "机构编码",
            dataIndex: "code",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "name",
            title: "机构名称",
            dataIndex: "name",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "nameAbbr",
            title: "机构简称",
            dataIndex: "nameAbbr",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "sortNo",
            title: "显示顺序",
            dataIndex: "sortNo",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            title: '操作',
            valueType: 'option',
            dataIndex: 'actions',
            render: (text, record:Organization, _, action) =>{
                return [
                    <a key='view' onClick={()=>{
                        setCurrentOrganization(record);
                        setDetailVisible(true);
                        }}>
                        查看
                    </a>,
                    rowExtraButtons({item:record}),
                ];
            }
        },
    ];

    return (
        <>
            <ProTable<Organization, Pagination>
                headerTitle="机构列表"
                rowKey="id"
                bordered={true}
                columns={columns}
                actionRef={tableActionRef}
                rowSelection={{
                    alwaysShowAlert:true,
                    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
                    defaultSelectedRowKeys: [],
                }}
                tableAlertRender={({ selectedRowKeys, selectedRows, onCleanSelected }) => (
                    <Space size={24}>
                          <span>
                            已选 {selectedRowKeys.length} 项
                            <a style={{ marginLeft: 8 }} onClick={onCleanSelected}>
                              取消选择
                            </a>
                          </span>
                        </Space>
                )}
                tableAlertOptionRender={({selectedRowKeys}) => {
                    return (
                        <Space size={16}>
                            <a onClick={async ()=>{
                                Modal.confirm({
                                    title: '删除机构',
                                    content: '确认删除所选机构吗？',
                                    okText: '删除所选',
                                    cancelText: '取消',
                                    onOk:async ()=>{
                                        const hide = message.loading("数据删除中....");
                                        for(let i=0; i<selectedRowKeys.length; i++){
                                            let id:string = selectedRowKeys[i]+"";
                                            try{
                                            await OrganizationApi.deleteOrganization({id:id});
                                            }catch(ex){

                                            }
                                        }
                                        hide();
                                        refreshData();
                                        message.success("数据删除完成！");
                                    }
                                });
                            }}>
                                批量删除
                            </a>
                        </Space>
                    );
                }}
                toolBarRender={()=>{
                    return [
                        <Button
                            type="primary"
                            onClick={showAddForm}
                        >
                            新建
                        </Button>
                    ];
                }}
                request={async (params={}, sort, filter)=>{

                    params.page = params.current;
                    const pageInfo = await OrganizationApi.findOrganizationPage({
                        deletedFlag: false,
                        rootId: rootId,
                        ...params,
                    });

                    pageInfo.list.forEach((item:any, idx:number)=>{
                        item.rowNo = ((params.current||1)-1)*(params.pageSize||20)+idx+1;
                    });

                    return {
                        success: true,
                        data: pageInfo.list,
                    };
                }}
            />

            <OrganizationAddForm
                visible={addFormVisible}
                onClose={hideAddForm}
                onRefresh={refreshData}
            />

            <OrganizationDetail
                visible={detailVisible}
                organization={currentOrganization}
                actionRef={detailActionRef}
                onClose={hideDetail}
                onRefresh={refreshData}
            />
        </>
    );
};


const OrganizationPage:React.FC = () => {

    const [rootId, setRootId] = useState<string>();
    const [treeData, setTreeData] = useState<Organization[]>([]);
    const [treeLoading, setTreeLoading] = useState<boolean>(false);

    const refreshTreeData = ()=>{
        setTreeLoading(true);
        OrganizationApi.findOrganizationTree({
            deletedFlag: false,
        }).then((treeList)=>{
            setTreeData(treeList);
        }).finally(()=>{
            setTreeLoading(false);
        });
    }

    useEffect(()=>{
        refreshTreeData();
    }, []);

    return (
        <ProCard split="vertical">
            <ProCard
                title={"机构列表"}
                headerBordered={true}
                colSpan={"240px"}
                bodyStyle={{paddingLeft:8, paddingRight:8}}
                extra={[
                    <Button
                        key="refresh"
                        type="link"
                        shape="circle"
                        icon={<RedoOutlined />}
                        onClick={()=>{
                                refreshTreeData();
                        }}
                    />
                ]}
            >
                <Spin spinning={treeLoading} tip="数据加载中..." >
                    <Tree
                        treeData={treeData}
                        fieldNames={{
                            title: 'name',
                            key: 'id',
                            children: 'children'
                        }}
                        onSelect={(selectedKeys)=>{
                            setRootId(selectedKeys[0]?selectedKeys[0].toString():"");
                        }}
                    />
                </Spin>
            </ProCard>
            <ProCard bodyStyle={{backgroundColor:'rgb(240,242,245)', padding:"0px 0px 0px 16px"}}>
                <OrganizationTable
                    refreshTreeData={refreshTreeData}
                    rootId={rootId}
                />
            </ProCard>
        </ProCard>
    );
}

export default OrganizationPage;